import { useEffect } from 'react'
import Styles from './TopBar.module.scss'
import { TopBarProps } from './Types'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { AlmanacFileBreadcrumbsContainer } from 'common/Breadcrumbs/AlmanacFileBreadcrumbs'
import { UnlockButton } from 'blaze-components/WriterTopBar/UnlockButton'
import { SearchIconButton } from 'blaze-components/WriterTopBar/SearchIconButton'
import { ShareButton } from './ShareButton'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { Button } from 'blaze-components/WriterTopBar/Button'
import { MoreDots } from 'blaze-ui/icons'
import { DesignMenuContainer } from 'components/DesignMenu/DesignMenuContainer'
import { NotificationsTrayContainer } from 'blaze-components/NotificationsTray/NotificationsTrayContainer'
import { NotificationsWriterTrigger } from 'blaze-components/NotificationsTray/NotificationsWriterTrigger'
import { CounterVariant } from 'blaze-ui/Counter/Counter'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { StatusButton } from './StatusButton'
import { ProfileDropdown } from 'blaze-components/TopBar/ProfileDropdown/ProfileDropdown'
import { VirtualMarketerUpgradeButton } from 'blaze-components/VirtualMarketerUpgradeButton/VirtualMarketerUpgradeButton'
import { CalendarButton } from 'blaze-components/WriterTopBar/CalendarButton'
import { BackButton } from 'blaze-components/WriterTopBar/BackButton'
import { useAuth } from 'hooks/UseAuth'
import BlazeLogo from 'components/WelcomeModal/blaze.svg'
import { ButtonLink } from 'blaze-components/WriterTopBar/Button'
import { Blaze } from 'blaze-ui/icons'
import { LOGIN_OR_SIGNUP_PATH } from 'paths'
import { AutopilotProUpgradeButton } from 'components/AutopilotUpgradeButtons'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { CreditUsageDropdown } from 'blaze-components/CreditUsageDropdown/CreditUsageDropdown'
import { ScheduledPostStatusTag } from 'components/ScheduledPostStatusTag/ScheduledPostStatusTag'

const TopBar = observer(({ fileId, canEdit }: TopBarProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { files } = useRootStore()
  const workspaces = useWorkspaces()
  const auth = useAuth()
  const currentWorkspace = useCurrentWorkspace()

  useEffect(() => {
    files.fetch(fileId)
  }, [files, fileId])

  const file = files.get(fileId)
  const isAssociatedWithPublishedAd = !!file?.related_ad_id

  // For guest users, use their first available workspace for ProfileDropdown
  const selectedWorkspaceForProfile = (() => {
    if (!file || !auth) return workspaces[0]?.id || 0

    const hasFileWorkspace = workspaces.some((w) => w.id === file.workspace.id)
    if (hasFileWorkspace) return file.workspace.id

    // Use the first available workspace for guest users
    return workspaces[0]?.id || 0
  })()

  // For backward compatibility, if canEdit is undefined, default to true for authenticated users
  const hasEditAccess = canEdit !== undefined ? canEdit : !!auth

  // Helper function to determine if user should see profile components
  const shouldShowProfileComponents = () => {
    if (!auth) return false
    // For authenticated users, show profile components regardless of workspace membership
    // This allows guest users to access their profile
    return true
  }

  return (
    <div className={Styles.wrapper}>
      {file && (
        <>
          {(() => {
            if (auth && hasEditAccess) {
              // Authenticated users with edit access - show full header
              return (
                <>
                  <div className={Styles.leftButtons}>
                    <div className={Styles.buttonGroup}>
                      <UnlockButton />
                      {!isSystemRedesignEnabled && <SearchIconButton />}
                      <BackButton iconOnly={isSystemRedesignEnabled} />
                      {isSystemRedesignEnabled && <CalendarButton iconOnly />}
                    </div>
                  </div>
                  <div className={Styles.centerButtons}>
                    <AlmanacFileBreadcrumbsContainer id={fileId} ancestors={file.ancestors} assetType="Design" />
                    {file.channel && !isSystemRedesignEnabled && <StatusButton file={file} />}
                    {isSystemRedesignEnabled && (
                      <ScheduledPostStatusTag scheduledPostId={file.scheduled_post_id} articleId={file.id} />
                    )}
                    {isSystemRedesignEnabled && <ShareButton fileId={fileId} variant="tertiary" iconOnly />}
                    {isSystemRedesignEnabled && (
                      <DropdownTrigger>
                        <IconButton icon={MoreDots} aria-label="Actions" variant="tertiary" />
                        {(close) => (
                          <DesignMenuContainer
                            id={file.id}
                            hiddenKeys={new Set(['rename', 'share', 'openInNewTab'])}
                            onAction={close}
                          />
                        )}
                      </DropdownTrigger>
                    )}
                  </div>
                  <div className={Styles.rightButtons}>
                    {!isSystemRedesignEnabled && (
                      <DropdownTrigger>
                        <Button icon={MoreDots} aria-label="Actions" />
                        {(close) => (
                          <DesignMenuContainer
                            id={file.id}
                            hiddenKeys={new Set(['rename', 'share', 'openInNewTab'])}
                            onAction={close}
                          />
                        )}
                      </DropdownTrigger>
                    )}
                    {!isSystemRedesignEnabled && <CalendarButton />}
                    {!isSystemRedesignEnabled && <ShareButton fileId={fileId} />}
                    {currentWorkspace?.is_ap_freemium ? (
                      <AutopilotProUpgradeButton variant="upgrade" />
                    ) : (
                      <VirtualMarketerUpgradeButton showTrialDaysLeft={false} />
                    )}
                    {shouldShowProfileComponents() && (
                      <>
                        {!isSystemRedesignEnabled && (
                          <NotificationsTrayContainer
                            trigger={<NotificationsWriterTrigger />}
                            counterVariant={CounterVariant.Dark}
                          />
                        )}
                        {isSystemRedesignEnabled && <CreditUsageDropdown />}
                        <ProfileDropdown selectedWorkspaceId={selectedWorkspaceForProfile} workspaces={workspaces} />
                      </>
                    )}
                  </div>
                </>
              )
            }

            if (auth && !hasEditAccess) {
              // Authenticated users without edit access - show navigation buttons and profile
              return (
                <>
                  <div className={Styles.leftButtons}>
                    <div className={Styles.buttonGroup}>
                      <UnlockButton />
                      <SearchIconButton />
                      {isAssociatedWithPublishedAd ? <BackButton /> : <CalendarButton />}
                    </div>
                  </div>
                  <div className={Styles.centerButtons}>
                    <AlmanacFileBreadcrumbsContainer id={fileId} ancestors={file.ancestors} assetType="Design" />
                    {file.channel && <StatusButton file={file} />}
                  </div>
                  <div className={Styles.rightButtons}>
                    {shouldShowProfileComponents() && (
                      <>
                        <NotificationsTrayContainer
                          trigger={<NotificationsWriterTrigger />}
                          counterVariant={CounterVariant.Dark}
                        />
                        <ProfileDropdown selectedWorkspaceId={selectedWorkspaceForProfile} workspaces={workspaces} />
                      </>
                    )}
                  </div>
                </>
              )
            }

            // Unauthenticated users - show simplified header
            return (
              <>
                <div className={Styles.leftButtons}>
                  <img src={BlazeLogo} alt="Blaze" style={{ height: '24px' }} />
                </div>
                <div className={Styles.centerButtons}>
                  <AlmanacFileBreadcrumbsContainer id={fileId} ancestors={file.ancestors} assetType="Design" />
                  {file.channel && <StatusButton file={file} />}
                </div>
                <div className={Styles.rightButtons}>
                  <ButtonLink to={LOGIN_OR_SIGNUP_PATH} icon={Blaze} aria-label="Try Blaze">
                    Try Blaze
                  </ButtonLink>
                </div>
              </>
            )
          })()}
        </>
      )}
    </div>
  )
})

export { TopBar }
